<template>
  <div>
    <a-card :bordered="false" class="header-solid h-full" :bodyStyle="{ paddingTop: 0, paddingBottom: '40px' }">
      <template #title>
        <h5 class="mb-0">My Profile</h5>
      </template>
      
      <div v-if="loading" class="text-center">
        <a-spin size="large" />
      </div>
      
      <div v-else>
        <!-- Profile Information -->
        <a-form :form="form" @submit="handleSubmit" layout="vertical">
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="Full Name" :colon="false">
                <a-input v-decorator="['full_name', { 
                  rules: [{ required: true, message: 'Please enter your full name!' }],
                  initialValue: user ? user.full_name : ''
                }]" placeholder="Enter your full name" />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Email" :colon="false">
                <a-input v-decorator="['email', { 
                  rules: [{ required: true, message: 'Please enter your email!' }],
                  initialValue: user ? user.email : ''
                }]" placeholder="Enter your email" disabled />
              </a-form-item>
            </a-col>
          </a-row>
          
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="Phone Number" :colon="false">
                <a-input v-decorator="['phone_number', { 
                  rules: [],
                  initialValue: user ? user.phone_number : ''
                }]" placeholder="Enter your phone number" />
              </a-form-item>
            </a-col>
            </a-row>
          

          
          <!-- Company Information - Only show for regular users -->
          <div v-if="!isAdminUser">
            <a-divider>Company Information</a-divider>
            
            <a-row :gutter="24">
              <a-col :span="12">
                <a-form-item label="Company Name" :colon="false">
                  <a-input v-decorator="['company_name', { 
                    rules: [],
                    initialValue: company ? company.name : ''
                  }]" placeholder="Enter your company name" disabled />
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item label="Company Address" :colon="false">
                  <a-input v-decorator="['company_address', { 
                    rules: [],
                    initialValue: company ? company.address : ''
                  }]" placeholder="Enter your company address" disabled />
                </a-form-item>
              </a-col>
            </a-row>
          </div>
          
          <!-- Submit Button -->
          <a-form-item>
            <a-button type="primary" html-type="submit" :loading="submitting">
              Update Profile
            </a-button>
          </a-form-item>
        </a-form>
      </div>
    </a-card>
  </div>
</template>

<script>
import userAPI from '../api/user';

export default {
  name: 'Profile',
  data() {
    return {
      loading: true,
      submitting: false,
      user: null,
      company: null,
      form: null,
    };
  },
  computed: {
    // Check if current user is admin
    isAdminUser() {
      return localStorage.getItem('userType') === 'admin';
    },
  },
  beforeCreate() {
    // Form creation moved to created() since this.$form is not available here
  },
  created() {
    // Initialize form after component is created and Vue is fully initialized
    this.form = this.$form.createForm(this, { name: 'profile_form' });
    
    // Ensure form is created before loading profile
    if (this.form) {
        this.loadProfile();
    } else {
      console.error('Failed to create form in created() lifecycle');
    }
  },
  mounted() {
    // Component is mounted, form should be ready
    console.log('Component mounted, form ready:', !!this.form);
    if (this.user && this.form) {
      // Try to populate form again in case it wasn't ready before
      this.$nextTick(() => {
        this.populateFormValues();
      });
    }
  },
  methods: {
    async loadProfile() {
      try {
        const response = await userAPI.getMyProfile();
        console.log('Profile response:', response);
        
        this.user = response;
        
        // Handle company data based on user type
        if (this.isAdminUser) {
          // Admin users don't have company information
          this.company = null;
        } else {
          // Regular users have company information
          this.company = response.company;
        }
        
        // Set form values after ensuring form is ready
        this.$nextTick(() => {
          this.populateFormValues();
        });
      } catch (error) {
        this.$message.error('Failed to load profile');
        console.error('Error loading profile:', error);
      } finally {
        this.loading = false;
      }
    },
    
    // Method to populate form values
    populateFormValues() {
      if (!this.form || !this.user) {
        console.log('Form or user not ready:', { form: !!this.form, user: !!this.user });
        return;
      }
      
      // Wait a bit more for the form to be fully rendered
      setTimeout(() => {
        this.setFormValuesWithRetry();
      }, 100);
    },
    
    // Method to set form values with retry mechanism
    setFormValuesWithRetry(retryCount = 0) {
      if (!this.form || !this.user) {
        console.log('Form or user not ready in retry:', { form: !!this.form, user: !!this.user });
        return;
      }
      
      try {
        // Prepare form values from API response
        const formValues = {
          full_name: this.user.full_name || '',
          email: this.user.email || '',
          phone_number: this.user.phone_number || '',
          us_dot_number: this.user.us_dot_number || '',
          mc_number: this.user.mc_number || '',
        };
        
        // Only add company fields for regular users
        if (!this.isAdminUser && this.company) {
          formValues.company_name = this.company.name || '';
          formValues.company_address = this.company.address || '';
        }
        
        console.log('Setting form values (attempt ' + (retryCount + 1) + '):', formValues);
        console.log('User data:', this.user);
        console.log('Form object:', this.form);
        
        // Check if form fields are actually rendered
        try {
          const formFields = this.form.getFieldsError();
          console.log('Available form fields:', formFields);
          
          // Try to get current form values to see what's there
          const currentValues = this.form.getFieldsValue();
          console.log('Current form values before setting:', currentValues);
        } catch (fieldError) {
          console.log('Could not get form fields yet, form might not be fully rendered');
        }
        
        // Try multiple approaches to set form values
        
        // Approach 1: Try setFieldsValue
        if (this.form.setFieldsValue) {
          try {
            this.form.setFieldsValue(formValues);
            console.log('Form values set successfully using setFieldsValue');
          } catch (setError) {
            console.log('setFieldsValue failed, trying alternative:', setError);
            
            // Approach 2: Try setting individual fields
            Object.keys(formValues).forEach(key => {
              if (formValues[key] !== undefined && formValues[key] !== null) {
                try {
                  this.form.setFieldsValue({ [key]: formValues[key] });
                  console.log(`Set field ${key} to:`, formValues[key]);
                } catch (fieldError) {
                  console.error(`Error setting field ${key}:`, fieldError);
                }
              }
            });
          }
        }
        
        // Verify values were set
        setTimeout(() => {
          try {
            const currentValues = this.form.getFieldsValue();
            console.log('Current form values after setting:', currentValues);
            
            // Check if all values were set correctly
            const allSet = Object.keys(formValues).every(key => 
              currentValues[key] === formValues[key]
            );
            
            if (!allSet && retryCount < 3) {
              console.log('Some values not set correctly, retrying...');
              this.setFormValuesWithRetry(retryCount + 1);
            } else if (allSet) {
              console.log('All form values set successfully!');
            } else {
              console.log('Form values still not set correctly after retries');
            }
          } catch (verifyError) {
            console.error('Error verifying form values:', verifyError);
          }
        }, 200);
        
      } catch (error) {
        console.error('Error setting form values:', error);
        
        // Retry if we haven't exceeded the limit
        if (retryCount < 3) {
          console.log('Retrying to set form values...');
          setTimeout(() => {
            this.setFormValuesWithRetry(retryCount + 1);
          }, 300);
        } else {
          // Fallback: try to set values individually
          this.setFormValuesFallback();
        }
      }
    },
    
    // Fallback method to set form values individually
    setFormValuesFallback() {
      if (!this.form || !this.user) return;
      
      try {
        const fields = ['full_name', 'email', 'phone_number'];
        
        fields.forEach(field => {
          if (this.user[field] !== undefined && this.user[field] !== null) {
            this.form.setFieldsValue({ [field]: this.user[field] });
          }
        });
        
        // Handle company fields for regular users
        if (!this.isAdminUser && this.company) {
          if (this.company.name) {
            this.form.setFieldsValue({ company_name: this.company.name });
          }
          if (this.company.address) {
            this.form.setFieldsValue({ company_address: this.company.address });
          }
        }
        
        console.log('Form values set using fallback method');
      } catch (error) {
        console.error('Fallback method also failed:', error);
      }
    },
    
    async handleSubmit(e) {
      e.preventDefault();
      
      this.form.validateFields(async (err, values) => {
        if (!err) {
          this.submitting = true;
          
          try {
            const updateData = {
              full_name: values.full_name,
              email: values.email,
              phone_number: values.phone_number,
            };
            
            await userAPI.updateMyProfile(updateData);
            
            this.$message.success('Profile updated successfully');
            
            // Reload profile data
            await this.loadProfile();
          } catch (error) {
            this.$message.error('Failed to update profile');
            console.error('Error updating profile:', error);
          } finally {
            this.submitting = false;
          }
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.header-solid {
  .ant-card-header {
    border-bottom: 1px solid #f0f0f0;
  }
}

.ant-divider {
  margin: 24px 0;
}
</style>
